// The line shell: a docked iPad, gloved hands, one question at a time.
//
// WHY THERE ARE TWO SHELLS AND NOT ONE RESPONSIVE LAYOUT. The prototype tried to
// serve a docked 1194x834 line iPad and an office desk from the same breakpoints
// and served neither: 1194px lands ABOVE the desktop breakpoint, so the line got
// the dense desk view, and the narrow breakpoint deleted the keypad — which is
// the input. A shell here is a different product, not a different width. Nothing
// in this file may grow a `lg:` escape hatch back toward the office layout.
//
// Three properties this shell guarantees its pages, and each is load-bearing:
//   - FULL VIEWPORT, NO PAGE SCROLL. A run's checks must not be something you can
//     scroll past. Pages get a fixed-height box and scroll inside it if they must.
//   - 56px MINIMUM TARGET. Everything here is pressed with a glove on, beside a
//     running line.
//   - THE SYNC CHIP AND THE HELD BANNER ARE CHROME, not a page's decision. The one
//     state an operator must never have to go looking for is "this device is
//     holding readings that are not going anywhere".
//
// ⚠ THE DOCK IS NOT ONLY AN iPAD. Receiving runs on whatever the warehouse holds,
// a phone included (end-to-end test, 22 Oct 2026: at 375px the header was 530px
// wide and Office and Hand over were off the screen). Below `sm` the secondary
// actions - Receiving, Office, Hand over - fold into a Menu button whose panel
// stacks them full width; the sync chip stays in the bar, because it is the one
// thing that must never be behind a tap. From `sm` up they sit in the bar and
// wrap rather than overflow. Every target stays 56px either way.
//
// Colour tokens come from app/src/index.css (the ramp in fleet-docs/quality.html).
// Red (--bad) means out-of-spec or rejected and nothing else — no error text, no
// destructive button in this shell borrows it.
import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { SyncChip } from '../components/SyncChip';
import { currentUpn } from '../config/auth';
import { api } from '../lib/api';
import { useDocumentTitle, usePageName } from '../lib/pageTitle';
import { handOverState, useQueue } from '../offline/useQueue';
import { useIirQueue } from '../offline/useIirQueue';
import { showReceiving } from '../pages/operator/receiveModel';
import { HeldBanner } from '../pages/operator/HeldBanner';
import { RefusedBanner } from '../pages/operator/RefusedBanner';
import { NewBuildNotice, OFFICE_ACTIONS, tokenUnavailableMessage, useMyGrants } from './OfficeShell';

/** Focus must be visible on a glossy screen under plant lighting. */
export const opFocus =
  'focus-visible:outline-hidden focus-visible:ring-4 focus-visible:ring-(--primary) ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-(--bg)';

/**
 * The message a failed call actually carried.
 *
 * The API's error bodies are `{ error: "<sentence>" }` and ApiError's own
 * `message` is only "API 403 Forbidden: /api/...". Showing that to an operator
 * tells them nothing they can act on, so the body's sentence wins. There is
 * deliberately no "something went wrong" fallback: if the server sent no
 * sentence, say which status it sent, because a person can repeat that to
 * somebody who can look it up.
 */
export function opMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const body = (err as { body?: unknown }).body;
    if (body && typeof body === 'object' && 'error' in body) {
      const sentence = (body as { error?: unknown }).error;
      if (typeof sentence === 'string' && sentence.trim()) return sentence;
    }
    // Same reasoning as status 0 below: a token that could not be issued means the
    // request never left the device, so it is not a refusal either.
    const signIn = tokenUnavailableMessage(err);
    if (signIn) return signIn;
    const status = (err as { status?: unknown }).status;
    const statusText = (err as { statusText?: unknown }).statusText;

    // ⚠⚠ STATUS 0 IS NOT A REFUSAL. IT IS THE REQUEST NEVER LEAVING THE DEVICE.
    // `ApiError` documents it exactly that way — "HTTP status, or 0 when the
    // request never reached the server" — and `typeof 0 === 'number'` is true,
    // so every dropped connection came out of here as "The server refused this
    // (0) and sent no message."
    //
    // In this app's own vocabulary "refused" is what the GATE does: no
    // competency, calibration overdue, line not released. Telling an operator the
    // server refused a reading it never saw sends them looking for a grant
    // problem while the real answer is that they walked past the dead spot at the
    // far end of the line. It fired hardest at the one moment the runbook asks
    // them to turn the wifi off on purpose.
    if (status === 0) {
      return 'The server could not be reached. The reading stays on this device until it can be.';
    }

    if (typeof status === 'number') {
      return `The server refused this (${status}${
        typeof statusText === 'string' && statusText ? ` ${statusText}` : ''
      }) and sent no message.`;
    }
  }
  if (err instanceof Error && err.message && !err.message.startsWith('API ')) return err.message;
  return 'The server could not be reached. The reading stays on this device until it can be.';
}

/**
 * What the sync chip counts: everything on this device still to leave it, from BOTH queues
 * (UI/UX audit, 31 Oct 2026, UX-09).
 *
 * WHY. The chip was fed by the readings queue alone, so on the dock it said "synced" while the
 * Receiving screen below it said "2 inspections waiting to send" - and the chip is the line the
 * person actually reads before walking away or handing over.
 *
 *   queued = readings (their photos included, queue.ts) + this account's IIR inspections still to
 *            send + their photos still to send.
 *   held   = what will NOT go by itself: another account's readings, reading photos, inspections
 *            and dock photos, plus this account's refused inspections and photos (kept on the
 *            device, waiting for their owner to Send again or Discard) and its refused line photos.
 *
 * ⚠ RULE 15: if ANY part of `queued` is unknown (null) the total is null - "queue unreadable" -
 * never the sum of the parts that could be read. A null folded into 0 is the chip saying "synced"
 * about a store it could not look in. Amber either way, never red (SyncChip).
 */
export function syncChipCounts(
  queue: { queued: number | null; held: readonly unknown[]; photosHeld: number; photosRefused: readonly unknown[] },
  iir: {
    pending: number | null;
    photosPending: number | null;
    held: readonly unknown[];
    photosHeld: number;
    refused: readonly unknown[];
    photosFailed: readonly unknown[];
  },
): { queued: number | null; held: number } {
  const queued =
    queue.queued === null || iir.pending === null || iir.photosPending === null ? null : queue.queued + iir.pending + iir.photosPending;
  const held =
    queue.held.length + queue.photosHeld + queue.photosRefused.length + iir.held.length + iir.photosHeld + iir.refused.length + iir.photosFailed.length;
  return { queued, held };
}

export function OperatorShell() {
  // The subtitle names the screen (UI/UX audit, 27 Oct 2026): the dock's form is not a line check.
  const receiving = useLocation().pathname.startsWith('/op/receive');
  const reading = useLocation().pathname.startsWith('/op/docs');
  // The tab title too (UI/UX audit, 31 Oct 2026, UX-21): this shell set none, so after the office the
  // tab still read "Goods in · Quality". The office's helper, with the screen's own record first.
  useDocumentTitle(usePageName(), reading ? 'Documents' : receiving ? 'Receiving' : 'Line check');
  // `accounts` is what makes this component re-render when the signed-in account
  // changes; `currentUpn()` reads the live MSAL account rather than a snapshot.
  // The pair is handed to the queue because a held capture's ONLY automatic exit
  // is its own account signing in on this device — the queue replays on a change
  // of this value, which is exactly the promise HeldBanner makes to the operator.
  const { instance, accounts } = useMsal();
  const signedInUpn = accounts.length > 0 ? currentUpn() : '';
  const queue = useQueue({ signedInUpn });

  /**
   * HANDOVER. A line iPad is shared, and every record in this app is attributed
   * to the UPN on the token — so without this control the second operator of the
   * shift captures readings under the first one's name, silently, and the
   * attribution model that everything else rests on is quietly false.
   *
   * ⚠ IT REFUSES WHILE ANYTHING IS QUEUED, and that is the point rather than a
   * limitation. A queued reading is stamped with the UPN that keyed it and is
   * HELD if a different account signs in — recoverable, but it needs the first
   * person back on this device to release it. Draining first turns a confusing
   * held pile into an ordinary handover. The number is shown so the outgoing
   * operator knows what they are waiting for rather than just being refused.
   */
  /**
   * ⚠⚠ AND IT REFUSES WHEN THE DEPTH IS UNKNOWN, WHICH IS NOT THE SAME AS ZERO.
   *
   * `queue.queued` is `null` when the device could not read its own queue. That
   * used to arrive as 0 — the queue answered zero when IndexedDB threw — so this
   * unlocked and the chip said "synced" on a device that could not see whether
   * anything was waiting. The operator handed the iPad over believing their
   * readings had been sent, and a queued row stamped with their UPN is HELD the
   * moment somebody else signs in.
   *
   * Refusing on unknown is the fail-closed direction: the worst case is somebody
   * waiting for a queue that was in fact empty, against stranding readings that
   * needed the outgoing person to stay signed in. `=== 0` alone would already
   * have been false for null — TypeScript permits the comparison — but relying on
   * that is relying on an accident, and the title below would have read
   * "null reading(s) still to send".
   */
  // The dock's inspections are stamped the same way (iirQueue.ts), so they count
  // too: pending, and refused ones still on the tablet waiting for their owner.
  const iir = useIirQueue(signedInUpn);
  // Unsent photos count as well (migration 019): only their owner can send them - the server
  // refuses a photo on somebody else's inspection - so a handover would strand them. The line's
  // refused photos too (migration 034): only their owner can Send again or Discard them.
  // One rule, shared with the office's Sign out: handOverState (useQueue.ts).
  const { depthKnown, toSend, canHandOver } = handOverState(queue, iir);
  // The chip counts the dock's queue too (UX-09): one answer to "has everything left this device?".
  const chip = syncChipCounts(queue, iir);

  /**
   * The way back to the office.
   *
   * It was missing entirely: the office shell linked to its own pages and this
   * one linked nowhere, so `/office` was a URL you had to know. Shown only to
   * people who hold an office grant, because a link an operator cannot use is
   * clutter on the one screen that must not have any — and the server refuses
   * regardless of what is rendered.
   */
  const grants = useMyGrants();
  // The SAME grants Landing sends to the office (audit 28 Oct 2026: RELEASE-only QA - Anna, Yi -
  // landed in the office, switched to Receiving, and had no way back).
  const showOffice = grants.known && OFFICE_ACTIONS.some((a) => grants.holds(a));
  // The dock screen, for whoever may record an incoming inspection (migration 017): the
  // vehicle inspection is RECEIVE's, the goods inspection after it RELEASE's (migration 033).
  // A grants read that FAILED is not "no grant" (rule 15): the link shows and the server decides.
  // Also whenever this tablet holds inspections still to send or refused - even for someone
  // without either - so nothing waits where nobody can see it (review round 2).
  const showReceive = showReceiving(
    grants.known ? grants.holds('RECEIVE') || grants.holds('RELEASE') : grants.error !== null,
    iir.pending === null || iir.photosPending === null ? null : iir.pending + iir.photosPending,
    iir.refused.length + iir.photosFailed.length,
  );
  const [documentsEnabled, setDocumentsEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    void Promise.resolve(api.get<{ enabled?: boolean }>('/documents/config'))
      .then((c) => (live ? setDocumentsEnabled(c?.enabled === true) : undefined))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  const [personOpen, setPersonOpen] = useState(false);
  const personRef = useRef<HTMLDivElement>(null);
  const path = useLocation().pathname;
  // The menu closes on Escape, on a tap outside it, and when the page changes.
  useEffect(() => setPersonOpen(false), [path]);
  useEffect(() => {
    if (!personOpen) return;
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setPersonOpen(false);
    const tap = (e: PointerEvent) => {
      if (personRef.current && !personRef.current.contains(e.target as Node)) setPersonOpen(false);
    };
    document.addEventListener('keydown', key);
    document.addEventListener('pointerdown', tap);
    return () => {
      document.removeEventListener('keydown', key);
      document.removeEventListener('pointerdown', tap);
    };
  }, [personOpen]);
  // No pilot banner (Amit, 27 Oct 2026). A pilot run still shows its PilotBadge on the line and Verify.

  /**
   * THE AREAS (Amit, 27-28 Oct 2026: "the menu looks very unfamiliar compared to other apps"). The
   * familiar shape: the three areas - Line check, Receiving, Office - as one SEGMENTED TAB BAR in the
   * header from `sm` up, and as a BOTTOM TAB BAR on a phone, where thumbs expect it. The current area
   * is the selected tab. What a person holds no grant for is not offered. Hand over (sign out) is not
   * an area: it lives in the person menu, behind the initial, like every other app's account menu.
   */
  const areaItems = [
    { to: '/op', label: 'Line check', current: !receiving && !reading, show: true },
    { to: '/op/receive', label: 'Receiving', current: receiving, show: showReceive },
    // Rule 37: only while document control is on. The read failing offers nothing and claims nothing.
    { to: '/op/docs', label: 'Documents', current: reading, show: documentsEnabled || reading },
    { to: '/office', label: 'Office', current: false, show: showOffice },
  ].filter((a) => a.show);

  // 56px, not 48 (UI/UX audit, 31 Oct 2026, UX-22): the header of this file promises a 56px
  // minimum target, and the area tabs and the initial were the two things in the chrome that broke it.
  const tabs = (
    <nav aria-label="Areas" className="hidden items-center rounded-(--radius) border border-(--border-strong) bg-(--bg) p-[3px] sm:flex">
      {areaItems.map((a) => (
        <Link
          key={a.to}
          to={a.to}
          aria-current={a.current ? 'page' : undefined}
          className={`flex min-h-[56px] items-center rounded-[calc(var(--radius)-3px)] px-(--s4) text-(length:--fs-sm) font-semibold ${
            a.current ? 'bg-(--card) text-(--fg) shadow-sm' : 'text-(--muted)'
          } ${opFocus}`}
        >
          {a.label}
        </Link>
      ))}
    </nav>
  );

  const handOverTitle = canHandOver
    ? `Signed in as ${signedInUpn}`
    : depthKnown
      ? `${toSend} reading(s), inspection(s) or photo(s) still to send or refused on this device. They are stamped with your name and cannot be sent by anyone else.`
      : 'This device cannot read its own queue, so we cannot tell whether anything is ' +
        'still to send. Stay signed in and tell a supervisor — handing over now could ' +
        'strand readings that only you can release.';
  const handOverLabel = canHandOver
    ? 'Hand over'
    : depthKnown
      ? `Hand over (${toSend} to send or refused)`
      : 'Hand over (queue unreadable)';

  /** The person menu: who is signed in, and Hand over. The initial carries a dot when something on
   *  this device still waits for them, so it is seen without opening the menu.
   *
   *  A DISCLOSURE, NOT A MENU (UI/UX audit, 31 Oct 2026, UX-46). It said `role="menu"` /
   *  `menuitem` / `aria-haspopup="menu"`, which promises arrow-key navigation and focus moving into
   *  the menu - none of which it does. A screen reader then announced a menu the keyboard could not
   *  drive. It is a button that shows and hides a panel: `aria-expanded` + `aria-controls` say
   *  exactly that, and Hand over inside it is an ordinary button, refused exactly as before. */
  const person = signedInUpn ? (
    <div ref={personRef} className="relative">
      <button
        type="button"
        aria-expanded={personOpen}
        aria-controls="op-person"
        aria-label={`Signed in as ${signedInUpn}`}
        onClick={() => setPersonOpen((o) => !o)}
        className={`relative flex h-[56px] w-[56px] items-center justify-center rounded-full border border-(--border-strong) bg-(--bg) text-(length:--fs-md) font-bold uppercase ${opFocus}`}
      >
        {signedInUpn.slice(0, 1)}
        {!canHandOver ? <span aria-hidden="true" className="absolute right-[2px] top-[2px] h-[10px] w-[10px] rounded-full bg-(--warn)" /> : null}
      </button>
      {personOpen ? (
        <div
          id="op-person"
          role="group"
          aria-label="Account"
          // 8px under the 56px initial (UX-22: it moved with the target, was top-[56px] under 48px).
          className="absolute right-0 top-[64px] z-30 flex w-[min(320px,calc(100vw-2*var(--s3)))] flex-col gap-(--s2) rounded-(--radius) border border-(--border-strong) bg-(--card) p-(--s3) shadow-lg"
        >
          <p className="m-0 text-(length:--fs-xs) text-(--muted)">Signed in as</p>
          <p className="m-0 truncate text-(length:--fs-sm) font-semibold" title={signedInUpn}>
            {signedInUpn}
          </p>
          <button
            type="button"
            disabled={!canHandOver}
            title={handOverTitle}
            onClick={() => {
              if (!canHandOver) return;
              void instance.logoutRedirect();
            }}
            className={`mt-(--s1) flex min-h-[56px] w-full items-center justify-start rounded-(--radius) border border-(--border-strong) bg-(--bg) px-(--s3) text-(length:--fs-sm) font-semibold disabled:opacity-50 ${opFocus}`}
          >
            {handOverLabel}
          </button>
          {!canHandOver ? <p className="m-0 text-(length:--fs-xs) text-(--muted)">{handOverTitle}</p> : null}
        </div>
      ) : null}
    </div>
  ) : null;

  /** The phone's bottom tab bar: the same areas, where thumbs expect them. */
  const bottomTabs =
    areaItems.length > 1 ? (
      <nav aria-label="Areas" className="flex flex-none border-t border-(--border) bg-(--card) sm:hidden">
        {areaItems.map((a) => (
          <Link
            key={a.to}
            to={a.to}
            aria-current={a.current ? 'page' : undefined}
            className={`flex min-h-[56px] flex-1 items-center justify-center border-t-2 text-(length:--fs-sm) font-semibold ${
              a.current ? 'border-(--fg) text-(--fg)' : 'border-transparent text-(--muted)'
            } ${opFocus}`}
          >
            {a.label}
          </Link>
        ))}
      </nav>
    ) : null;

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden bg-(--bg) text-(--fg)">
      {/* One header row on every width: brand and area, the tabs (from sm), the sync dot, the person. */}
      <header className="relative flex flex-none items-center gap-(--s3) border-b border-(--border) bg-(--card) px-(--s3) py-(--s2) sm:gap-(--s4) sm:px-(--s4)">
        <div className="flex min-w-0 items-baseline gap-(--s2) sm:gap-(--s3)">
          <span className="text-(length:--fs-md) font-bold tracking-tight sm:text-(length:--fs-lg)">Quality</span>
          {/* The area's name on a phone, where the tabs sit at the bottom. */}
          <span className="truncate text-(length:--fs-xs) text-(--muted) sm:hidden">{reading ? 'Documents' : receiving ? 'Receiving' : 'Line check'}</span>
        </div>
        <div className="mx-auto">{tabs}</div>
        <div className="ml-auto flex flex-none items-center gap-(--s2) sm:ml-0 sm:gap-(--s3)">
          <SyncChip queued={chip.queued} held={chip.held} online={queue.online} />
          {person}
        </div>
      </header>


      {/* Mounted unconditionally: the banner decides whether it has anything to
          say. Mounting it only when something is held would make its absence mean
          two different things — nothing held, or the shell forgot to ask.
          `queue.heldOwner` is null when the held items come from more than one
          account, and the banner says "another account" rather than naming one of
          several and sending the wrong person to sign in. */}
      <NewBuildNotice large />

      <HeldBanner items={queue.held} owner={queue.heldOwner} onDiscard={queue.discardHeld} />

      {/* Another account's line photos (migration 034), held with their readings: only that person
          can send or discard them, so the next one at the tablet is told they are here. */}
      {queue.photosHeld > 0 ? (
        <p role="status" className="m-0 flex-none border-b border-(--border) bg-(--card) px-(--s4) py-(--s2) text-(length:--fs-sm)">
          {queue.photosHeld} photo{queue.photosHeld === 1 ? '' : 's'} taken by another account{' '}
          {queue.photosHeld === 1 ? 'is' : 'are'} held here until that person signs in.
        </p>
      ) : null}

      {/* ⚠ ABOVE THE PAGE, NOT INSIDE ONE, and mounted unconditionally for the
          same reason as HeldBanner. A refusal arrives during a REPLAY, which can
          happen on any screen and often on none the operator is looking at — the
          capture was made on the walk-around, the wifi came back at the keypad.
          A banner that only existed on the screen where the reading was taken
          would be the silent loss all over again, one navigation later. */}
      <RefusedBanner
        items={queue.refusals}
        dropped={queue.refusalsDropped}
        onAcknowledge={queue.acknowledgeRefusals}
      />

      {/* THE PAGES ARE CHILD ROUTES DECLARED IN App.tsx, and this is where they
          render. Declaring a second <Routes> here instead would draw nothing at
          all: React Router only matches a descendant <Routes> when the parent
          route path ends in `*`, and App mounts this shell at `/op` with real
          children — so the child route would render into an <Outlet/> that does
          not exist while the descendant <Routes> matched nothing. Header, chip
          and banner, over an empty body.

          The routes themselves — `/op` (LinePick) and `/op/run/:runId`
          (LineCheck, which reads `runId` with useParams) — live in App.tsx. */}
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Outlet />
      </main>
      {bottomTabs}
    </div>
  );
}

export default OperatorShell;
